import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import "../styles/HierarchicalDatePicker.css";

const HierarchicalDatePicker = ({
  selectedDate,
  onChange,
  minDate,
  maxDate,
  onClose, // We will pass an onClose function to handle the outside click
}) => {
  const [currentView, setCurrentView] = useState("day"); // 'day', 'month', 'year'
  const [displayDate, setDisplayDate] = useState(selectedDate || new Date());

  const currentYear = displayDate.getFullYear();
  const currentMonth = displayDate.getMonth();
  const currentDay = displayDate.getDate();

  const calendarRef = useRef(null);

  // Close the calendar if clicked outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        onClose(); // Trigger close when clicked outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  // Generate a range of years for selection (e.g., +/- 10 years from current)
  const generateYearRange = () => {
    const startYear = currentYear - 10;
    const endYear = currentYear + 10;
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  // Generate months array
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  // Generate days for the current month
  const generateDays = () => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const startingDay = firstDayOfMonth.getDay(); // 0 (Sun) to 6 (Sat)
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    const weeks = [];
    let currentWeek = [];
    let dayCounter = 1;

    // Fill the first week
    for (let i = 0; i < 7; i++) {
      if (i < startingDay) {
        currentWeek.push(null);
      } else {
        currentWeek.push(dayCounter++);
      }
    }
    weeks.push(currentWeek);

    // Fill subsequent weeks
    while (dayCounter <= daysInMonth) {
      currentWeek = [];
      for (let i = 0; i < 7; i++) {
        if (dayCounter > daysInMonth) {
          currentWeek.push(null);
        } else {
          currentWeek.push(dayCounter++);
        }
      }
      weeks.push(currentWeek);
    }
    return weeks;
  };

  // Navigation
  const handlePrev = () => {
    if (currentView === "day") {
      setDisplayDate(new Date(currentYear, currentMonth - 1, 1));
    } else if (currentView === "month") {
      setDisplayDate(new Date(currentYear - 1, currentMonth, 1));
    } else {
      setDisplayDate(new Date(currentYear - 10, currentMonth, 1));
    }
  };

  const handleNext = () => {
    if (currentView === "day") {
      setDisplayDate(new Date(currentYear, currentMonth + 1, 1));
    } else if (currentView === "month") {
      setDisplayDate(new Date(currentYear + 1, currentMonth, 1));
    } else {
      setDisplayDate(new Date(currentYear + 10, currentMonth, 1));
    }
  };

  // Helper function to check if date is out of min/max range
  const isDisabledDate = (dateObj) => {
    if (!dateObj) return true; // Null or invalid
    if (minDate && dateObj < minDate) return true;
    if (maxDate && dateObj > maxDate) return true;
    return false;
  };

  // Year / Month / Day Selections
  const handleYearSelect = (year) => {
    setDisplayDate(new Date(year, currentMonth, 1));
    setCurrentView("month");
  };

  const handleMonthSelect = (monthIndex) => {
    setDisplayDate(new Date(currentYear, monthIndex, 1));
    setCurrentView("day");
  };

  const handleDaySelect = (day) => {
    const chosenDate = new Date(currentYear, currentMonth, day);
    if (!isDisabledDate(chosenDate)) {
      onChange(chosenDate);
    }
  };

  // For heading label
  let headingLabel = "";
  if (currentView === "day") {
    headingLabel = `${months[currentMonth]} ${currentYear}`;
  } else if (currentView === "month") {
    headingLabel = `${currentYear}`;
  } else {
    headingLabel = `Select Year`;
  }

  return (
    <div className="hierarchical-date-picker" ref={calendarRef}>
      {/* Header */}
      <div className="date-picker-header">
        <button className="nav-button" onClick={handlePrev}>
          &#8249;
        </button>
        <div
          className="current-display"
          onClick={() => {
            // day -> month, month -> year
            if (currentView === "day") setCurrentView("month");
            else if (currentView === "month") setCurrentView("year");
          }}
          role="button"
          tabIndex={0}
        >
          {headingLabel}
        </div>
        <button className="nav-button" onClick={handleNext}>
          &#8250;
        </button>
      </div>

      {/* Body */}
      <div className="date-picker-body">
        {currentView === "year" && (
          <div className="years-grid">
            {generateYearRange().map((yr) => (
              <div
                key={yr}
                className={`year-option ${yr === currentYear ? "selected" : ""}`}
                onClick={() => handleYearSelect(yr)}
                role="button"
                tabIndex={0}
              >
                {yr}
              </div>
            ))}
          </div>
        )}

        {currentView === "month" && (
          <div className="months-grid">
            {months.map((m, idx) => (
              <div
                key={m}
                className={`month-option ${idx === currentMonth ? "selected" : ""}`}
                onClick={() => handleMonthSelect(idx)}
                role="button"
                tabIndex={0}
              >
                {m}
              </div>
            ))}
          </div>
        )}

        {currentView === "day" && (
          <>
            <div className="day-names">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="day-name">
                  {d}
                </div>
              ))}
            </div>
            <div className="days-grid">
              {generateDays().map((week, wIndex) => (
                <div key={wIndex} className="week-row">
                  {week.map((day, dIndex) => {
                    if (!day) {
                      return <div key={dIndex} className="day-option empty"></div>;
                    }
                    const dayDate = new Date(currentYear, currentMonth, day);
                    const isToday =
                      dayDate.toDateString() === new Date().toDateString();
                    const isDisabled = isDisabledDate(dayDate);

                    return (
                      <div
                        key={dIndex}
                        className={`day-option
                          ${isToday ? "today" : ""}
                          ${isDisabled ? "disabled" : ""}
                        `}
                        onClick={() => !isDisabled && handleDaySelect(day)}
                        role="button"
                        tabIndex={0}
                      >
                        {day}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

HierarchicalDatePicker.propTypes = {
  selectedDate: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  onClose: PropTypes.func.isRequired, // onClose function to handle closing
};

HierarchicalDatePicker.defaultProps = {
  selectedDate: new Date(),
  minDate: null,
  maxDate: null,
};

export default HierarchicalDatePicker;
